import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Chip,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowLeft, ChevronRight, Plus, FileSpreadsheet, FileText,
  ShoppingCart, PackageCheck, Save, Package, Store,
  Clock, CheckCircle, AlertTriangle, Lock, DollarSign,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import toast from 'react-hot-toast';

const API_BASE = 'https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com';

const PLATFORM_META = {
  Shopify: { color: '#10b981', bg: 'rgba(16,185,129,0.06)', icon: Package },
  Amazon: { color: '#f59e0b', bg: 'rgba(245,158,11,0.06)', icon: ShoppingCart },
  Walmart: { color: '#2383e2', bg: 'rgba(35,131,226,0.06)', icon: Store },
};

const getStatusMeta = (status) => {
  switch (status) {
    case 'Open': return { color: '#f59e0b', icon: <Clock size={14} />, label: 'Open' };
    case 'Order Placed': return { color: '#10b981', icon: <CheckCircle size={14} />, label: 'Order Placed' };
    case 'Backordered': return { color: '#ef4444', icon: <AlertTriangle size={14} />, label: 'Backordered' };
    case 'Closed': return { color: '#6b7280', icon: <Lock size={14} />, label: 'Closed' };
    default: return { color: '#787774', icon: null, label: status };
  }
};

const MasterPODetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:768px)');

  const ALL_PLATFORMS = ["Shopify", "Walmart", "Amazon"];
  const [missingPlatforms, setMissingPlatforms] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  const [masterPO, setMasterPO] = useState(null);
  const [childPODetails, setChildPODetails] = useState([]);
  const [aggregatedProducts, setAggregatedProducts] = useState([]);
  const [poName, setPOName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedChildPO] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);

  const formatPrice = (price) => typeof price === 'number' ? price.toFixed(2) : '-';

  const getChildProductTotal = (childProduct) => {
    if (childProduct.totalPrice !== undefined && childProduct.totalPrice !== null) return childProduct.totalPrice;
    if (childProduct.quantityOrdered && childProduct.unitPrice) return childProduct.quantityOrdered * childProduct.unitPrice;
    return 0;
  };

  const handleGoBack = () => {
    if (location.state?.from) navigate(location.state.from);
    else if (window.history.length > 2) navigate(-1);
    else navigate('/master-pos');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const masterRes = await axios.get(`${API_BASE}/api/master-pos/${id}`);
        const masterData = masterRes.data;
        setMasterPO(masterData);
        setPOName(masterData.poName || '');

        const aggRes = await axios.get(`${API_BASE}/api/master-pos/${id}/products`);
        setAggregatedProducts(aggRes.data);

        const childIds = masterData.childPos || [];
        const childPromises = childIds.map((childId) => axios.get(`${API_BASE}/api/pos/${childId}`));
        const childRes = await Promise.all(childPromises);
        setChildPODetails(childRes.map((res) => res.data));
      } catch (err) {
        setError('Failed to fetch data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleSaveName = async () => {
    const t = toast.loading('Saving name…');
    try {
      await axios.put(`${API_BASE}/api/master-pos/${id}`, { poName });
      toast.success('PO name updated', { id: t });
    } catch (err) {
      toast.error('Failed to update PO name', { id: t });
    }
  };

  const handleOrderPlaced = async () => {
    const t = toast.loading('Updating status…');
    try {
      await axios.put(`${API_BASE}/api/master-pos/${id}/order-placed`);
      toast.success('Marked as Order Placed', { id: t });
      window.location.reload();
    } catch (err) {
      toast.error('Failed to update order status', { id: t });
    }
  };

  const exportToExcel = () => {
    if (!masterPO || !aggregatedProducts) return;
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US');
    const COMPANY_NAME = masterPO.companyName || "Track Commerce";
    const PO_NUMBER = masterPO.poName;
    const VENDOR_INFO = masterPO.vendorName || "MHC";
    const ORDER_DATE = masterPO.combinedOrderDate
      ? new Date(masterPO.combinedOrderDate).toLocaleDateString("en-US")
      : formattedDate;
    const STATUS = masterPO.status || "Draft";

    const sheetData = [
      [`PURCHASE ORDER`], [`Date: ${ORDER_DATE}`], [`Company: ${COMPANY_NAME}`],
      [`PO Number: ${PO_NUMBER}`], [`Vendor: ${VENDOR_INFO}`], [`Status: ${STATUS}`],
      [], ["Product Name", "Quantity", "Unit Price", "Total Price"],
      ...aggregatedProducts.map((product) => {
        const truncatedName = (product.productTitle || "Unnamed Product").slice(0, 6);
        return [truncatedName, product.quantityOrdered || 0, Number(product.unitPrice || 0).toFixed(2), Number(product.totalPrice || 0).toFixed(2)];
      }),
      ["TOTAL", aggregatedProducts.reduce((s, p) => s + (p.quantityOrdered || 0), 0), "",
        aggregatedProducts.reduce((s, p) => s + Number(p.totalPrice || 0), 0).toFixed(2)],
    ];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    ws["!merges"] = Array.from({ length: 6 }, (_, i) => ({ s: { r: i, c: 0 }, e: { r: i, c: 3 } }));
    ws["!cols"] = [{ wch: 15 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchase Order");
    XLSX.writeFile(wb, `PO_${ORDER_DATE.replace(/\//g, "-")}.xlsx`);
  };

  const exportToPDF = () => {
    if (!masterPO || !aggregatedProducts) return;
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-US");
    const COMPANY_NAME = masterPO.companyName || "Track Commerce";
    const PO_NUMBER = masterPO.poName;
    const VENDOR_INFO = masterPO.vendorName || "MHC";
    const ORDER_DATE = masterPO.combinedOrderDate
      ? new Date(masterPO.combinedOrderDate).toLocaleDateString("en-US") : formattedDate;

    const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 50;
    const colors = { primary: [41, 82, 156], secondary: [245, 247, 250], accent: [76, 175, 80], text: [33, 37, 41], lightText: [108, 117, 125], border: [222, 226, 230], white: [255, 255, 255] };

    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 120, "F");
    doc.setFillColor(...colors.accent);
    doc.rect(0, 115, pageWidth, 5, "F");
    doc.setFontSize(24); doc.setTextColor(...colors.white); doc.setFont("helvetica", "bold");
    doc.text(COMPANY_NAME, margin, 65);
    doc.setFontSize(28);
    const titleWidth = doc.getTextWidth("PURCHASE ORDER");
    doc.text("PURCHASE ORDER", pageWidth - titleWidth - margin, 65);
    doc.setFontSize(12); doc.setFont("helvetica", "normal"); doc.setTextColor(240, 240, 240);
    doc.text("Official Document", pageWidth - doc.getTextWidth("Official Document") - margin, 85);

    const infoStartY = 160;
    doc.setFillColor(...colors.secondary);
    doc.rect(margin, infoStartY - 10, (pageWidth - 3 * margin) / 2, 100, "F");
    doc.rect(margin + (pageWidth - 3 * margin) / 2 + margin / 2, infoStartY - 10, (pageWidth - 3 * margin) / 2, 100, "F");

    doc.setFontSize(11); doc.setTextColor(...colors.text); doc.setFont("helvetica", "bold");
    doc.text("ORDER INFORMATION", margin + 15, infoStartY + 10);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.setTextColor(...colors.lightText); doc.text("Order Date:", margin + 15, infoStartY + 30);
    doc.setTextColor(...colors.text); doc.text(ORDER_DATE, margin + 80, infoStartY + 30);
    doc.setTextColor(...colors.lightText); doc.text("PO Number:", margin + 15, infoStartY + 50);
    doc.setTextColor(...colors.text); doc.text(PO_NUMBER, margin + 80, infoStartY + 50);

    const rightColX = margin + (pageWidth - 3 * margin) / 2 + margin / 2 + 15;
    doc.setFontSize(11); doc.setTextColor(...colors.text); doc.setFont("helvetica", "bold");
    doc.text("VENDOR INFORMATION", rightColX, infoStartY + 10);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.setTextColor(...colors.lightText); doc.text("Vendor Name:", rightColX, infoStartY + 50);
    doc.setTextColor(...colors.text); doc.text(VENDOR_INFO, rightColX + 75, infoStartY + 50);

    const tableStartY = infoStartY + 120;
    doc.setFontSize(14); doc.setTextColor(...colors.text); doc.setFont("helvetica", "bold");
    doc.text("ORDER DETAILS", margin, tableStartY);

    const tableRows = aggregatedProducts.map((p) => [
      (p.productTitle || "Unnamed").slice(0, 6),
      String(p.quantityOrdered || 0),
      `$${Number(p.unitPrice || 0).toFixed(2)}`,
      `$${Number(p.totalPrice || 0).toFixed(2)}`,
    ]);
    const totalAmount = aggregatedProducts.reduce((s, p) => s + Number(p.totalPrice || 0), 0);
    tableRows.push(["", "", "SUBTOTAL:", `$${totalAmount.toFixed(2)}`]);
    tableRows.push(["", "", "TOTAL:", `$${totalAmount.toFixed(2)}`]);

    doc.autoTable({
      startY: tableStartY + 25, margin: { left: margin },
      head: [["Product Name", "Quantity", "Unit Price", "Total Price"]],
      body: tableRows, theme: 'plain',
      styles: { font: "helvetica", fontSize: 9, cellPadding: { top: 8, right: 8, bottom: 8, left: 8 }, lineColor: colors.border, lineWidth: 0.5, textColor: colors.text },
      headStyles: { fillColor: colors.primary, textColor: colors.white, fontStyle: "bold", fontSize: 10, halign: "center" },
      bodyStyles: { fillColor: colors.white },
      alternateRowStyles: { fillColor: [252, 252, 252] },
      columnStyles: { 0: { cellWidth: 140, halign: "left" }, 1: { cellWidth: 90, halign: "center" }, 2: { cellWidth: 130, halign: "right" }, 3: { cellWidth: 130, halign: "right" } },
      didParseCell: (data) => {
        const rowIndex = data.row.index;
        if (data.section === "body" && rowIndex >= tableRows.length - 2) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = rowIndex === tableRows.length - 1 ? colors.primary : colors.secondary;
          data.cell.styles.textColor = rowIndex === tableRows.length - 1 ? colors.white : colors.text;
          if (data.column.index < 2) data.cell.text = [];
        }
      },
    });

    const finalY = doc.lastAutoTable.finalY || tableStartY + 200;
    doc.setDrawColor(...colors.border); doc.setLineWidth(1);
    doc.line(margin, finalY + 40, pageWidth - margin, finalY + 40);
    doc.setFontSize(8); doc.setTextColor(...colors.lightText); doc.setFont("helvetica", "normal");
    doc.text("This is an official purchase order document. Please retain for your records.", margin, finalY + 55);
    doc.text(`Generated on ${formattedDate} | PO#: ${PO_NUMBER}`, margin, finalY + 70);

    doc.save(`PurchaseOrder_${PO_NUMBER.replace(/[^a-zA-Z0-9]/g, "_")}_${ORDER_DATE.replace(/\//g, "-")}.pdf`);
  };

  const handleEditPO = (childId) => navigate(`/po-edit/${childId}`);

  const handleDeletePO = async () => {
    if (!selectedChildPO) return;
    const t = toast.loading('Removing list…');
    try {
      await axios.put(`${API_BASE}/api/master-pos/${id}/remove-child/${selectedChildPO}`);
      toast.success('Child PO removed', { id: t });
      window.location.reload();
    } catch (err) {
      toast.error('Failed to remove child PO', { id: t });
    }
    setOpenDeleteDialog(false);
  };

  const handleOpenAddModal = () => {
    const have = childPODetails.map((c) => c.platform);
    setMissingPlatforms(ALL_PLATFORMS.filter((plat) => !have.includes(plat)));
    setOpenAddModal(true);
  };

  const handleCloseAddModal = () => { setOpenAddModal(false); setSelectedPlatforms([]); };

  const togglePlatform = (plat) =>
    setSelectedPlatforms((prev) => prev.includes(plat) ? prev.filter((p) => p !== plat) : [...prev, plat]);

  const handleAddPlatforms = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    const t = toast.loading('Adding platforms…');
    try {
      await axios.post(`${API_BASE}/api/master-pos/${id}/add-platforms`, { platforms: selectedPlatforms });
      toast.success('Platforms added', { id: t });
      handleCloseAddModal();
      window.location.reload();
    } catch (err) {
      toast.error('Failed to add platforms', { id: t });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress size={32} thickness={3} />
      </Box>
    );
  }

  if (error && !masterPO) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;
  if (!masterPO) return <Typography>Loading Purchase Order details...</Typography>;

  const statusMeta = getStatusMeta(masterPO.status);
  const grandTotal = aggregatedProducts.reduce((acc, p) => acc + (p.totalPrice || 0), 0);
  const totalQty = aggregatedProducts.reduce((acc, p) => acc + (p.quantityOrdered || 0), 0);

  return (
    <Box className="fade-in">
      <Button onClick={handleGoBack} startIcon={<ArrowLeft size={16} />} sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}>
        Back
      </Button>

      {/* Header Card */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ height: 4, bgcolor: statusMeta.color }} />
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Chip
                  size="small"
                  icon={statusMeta.icon}
                  label={statusMeta.label}
                  sx={{
                    height: 24, fontSize: '0.72rem', fontWeight: 600,
                    bgcolor: `${statusMeta.color}12`, color: statusMeta.color,
                    border: `1px solid ${statusMeta.color}28`,
                    '& .MuiChip-icon': { color: statusMeta.color },
                  }}
                />
                <Chip
                  size="small"
                  label={masterPO.paid ? 'Paid' : 'Unpaid'}
                  sx={{
                    height: 24, fontSize: '0.72rem', fontWeight: 600,
                    bgcolor: masterPO.paid ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                    color: masterPO.paid ? '#10b981' : '#ef4444',
                    border: `1px solid ${masterPO.paid ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', mb: 0.5 }}>
                <TextField
                  value={poName}
                  onChange={(e) => setPOName(e.target.value)}
                  variant="standard"
                  sx={{
                    flex: 1, maxWidth: 400,
                    '& .MuiInput-input': { fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.02em', py: 0.25 },
                    '& .MuiInput-underline:before': { borderColor: 'transparent' },
                    '& .MuiInput-underline:hover:before': { borderColor: 'divider' },
                  }}
                />
                <Button
                  size="small"
                  startIcon={<Save size={14} />}
                  onClick={handleSaveName}
                  sx={{ fontSize: '0.75rem', flexShrink: 0 }}
                >
                  Save
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Created {masterPO.combinedOrderDate ? new Date(masterPO.combinedOrderDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
              </Typography>
            </Box>

            {/* Summary numbers */}
            <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
              <Box sx={{ textAlign: 'center', px: 2, py: 1, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>
                  Products
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
                  {aggregatedProducts.length}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, py: 1, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>
                  Total Qty
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
                  {totalQty}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, py: 1, borderRadius: 1.5, bgcolor: 'rgba(35,131,226,0.06)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>
                  Grand Total
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.15rem', color: 'primary.main' }}>
                  ${formatPrice(grandTotal)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Action buttons - inside header */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button size="small" variant="outlined" startIcon={<FileSpreadsheet size={14} />} onClick={exportToExcel}
              sx={{ fontSize: '0.75rem', borderColor: '#10b981', color: '#10b981', '&:hover': { bgcolor: 'rgba(16,185,129,0.06)', borderColor: '#10b981' } }}>
              Excel
            </Button>
            <Button size="small" variant="outlined" startIcon={<FileText size={14} />} onClick={exportToPDF}
              sx={{ fontSize: '0.75rem', borderColor: '#6366f1', color: '#6366f1', '&:hover': { bgcolor: 'rgba(99,102,241,0.06)', borderColor: '#6366f1' } }}>
              PDF
            </Button>
            {masterPO.status === 'Open' && (
              <Button size="small" variant="contained" startIcon={<ShoppingCart size={14} />} onClick={handleOrderPlaced}
                sx={{ fontSize: '0.75rem', bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' }, ml: 'auto' }}>
                Mark Order Placed
              </Button>
            )}
            {(masterPO.status === 'Order Placed' || masterPO.status === 'Backordered' || masterPO.status === 'Closed') && (
              <Button size="small" variant="contained" startIcon={<PackageCheck size={14} />} onClick={() => navigate(`/master-po/${id}/receive`)}
                sx={{ fontSize: '0.75rem', bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, ml: 'auto' }}>
                Receive Order
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Child Purchase Lists */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            Platform Lists
          </Typography>
          {masterPO.status === 'Open' && (
            <Button size="small" variant="outlined" startIcon={<Plus size={14} />} onClick={handleOpenAddModal} sx={{ fontSize: '0.75rem' }}>
              Add Platform
            </Button>
          )}
        </Box>

        {childPODetails.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">No platform lists found.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 1.5 }}>
            {childPODetails.map((po) => {
              const childTotal = (po.products || []).reduce((sum, prod) => sum + getChildProductTotal(prod), 0);
              const meta = PLATFORM_META[po.platform] || { color: '#787774', bg: 'transparent', icon: Package };
              const PlatformIcon = meta.icon;

              return (
                <motion.div key={po._id} whileHover={{ y: -2 }} whileTap={{ scale: 0.99 }}>
                  <Box
                    onClick={() => handleEditPO(po._id)}
                    sx={{
                      p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                      cursor: 'pointer', position: 'relative', overflow: 'hidden',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      '&:hover': { borderColor: meta.color, boxShadow: `0 2px 12px ${meta.color}12` },
                    }}
                  >
                    <Box sx={{ height: 3, bgcolor: meta.color, position: 'absolute', top: 0, left: 0, right: 0 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: 0.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PlatformIcon size={15} style={{ color: meta.color }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                          {po.platform}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                          {po.products?.length || 0} products
                        </Typography>
                      </Box>
                      <ChevronRight size={16} style={{ color: '#b4b4b0' }} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {po.createdAt ? new Date(po.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: meta.color }}>
                        ${formatPrice(childTotal)}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Aggregated Products Table */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 2 }}>
          All Products
        </Typography>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            {/* Header */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: `2fr repeat(${3 + childPODetails.length}, 1fr)`,
              px: 2, py: 1.25, minWidth: 600,
              borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(55,53,47,0.024)',
            }}>
              {['Product', 'Qty', 'Unit $', 'Total',
                ...childPODetails.map(c => c.platform || 'PO')
              ].map((h, i) => (
                <Typography key={`${h}-${i}`} variant="caption" sx={{
                  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
                  fontSize: '0.65rem', color: 'text.secondary',
                }}>{h}</Typography>
              ))}
            </Box>

            {/* Rows */}
            {aggregatedProducts.map((product, rowIdx) => (
              <Box key={product.productId} sx={{
                display: 'grid',
                gridTemplateColumns: `2fr repeat(${3 + childPODetails.length}, 1fr)`,
                px: 2, py: 1.25, minWidth: 600,
                borderBottom: rowIdx < aggregatedProducts.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
                alignItems: 'center',
                '&:hover': { bgcolor: 'action.hover' },
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{product.productTitle}</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{product.quantityOrdered}</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>${formatPrice(product.unitPrice)}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>${formatPrice(product.totalPrice)}</Typography>
                {childPODetails.map((child) => {
                  const cp = child.products?.find((p) => String(p.productId) === String(product.productId));
                  const childTotal = cp ? getChildProductTotal(cp) : null;
                  return (
                    <Typography key={`${child._id}-${product.productId}`} variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {childTotal !== null && childTotal !== 0 ? `$${formatPrice(childTotal)}` : '-'}
                    </Typography>
                  );
                })}
              </Box>
            ))}

            {/* Grand Total Row */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: `2fr repeat(${3 + childPODetails.length}, 1fr)`,
              px: 2, py: 1.25, minWidth: 600,
              bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(55,53,47,0.024)',
              borderTop: '1px solid', borderColor: 'divider',
            }}>
              <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.8rem' }}>GRAND TOTAL</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.8rem' }}>{totalQty}</Typography>
              <Typography variant="body2" />
              <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.8rem', color: 'primary.main' }}>
                ${formatPrice(grandTotal)}
              </Typography>
              {childPODetails.map((child) => {
                const childTotal = aggregatedProducts.reduce((acc, prod) => {
                  const cp = child.products?.find((p) => String(p.productId) === String(prod.productId));
                  return acc + (cp ? getChildProductTotal(cp) : 0);
                }, 0);
                return (
                  <Typography key={`${child._id}-total`} variant="body2" sx={{ fontWeight: 800, fontSize: '0.8rem' }}>
                    ${formatPrice(childTotal)}
                  </Typography>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>

      {/* Add Platforms Dialog */}
      <Dialog open={openAddModal} onClose={handleCloseAddModal}>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Platform Lists</DialogTitle>
        <DialogContent dividers>
          {missingPlatforms.length > 0 ? (
            missingPlatforms.map((plat) => (
              <FormControlLabel
                key={plat}
                control={<Checkbox checked={selectedPlatforms.includes(plat)} onChange={() => togglePlatform(plat)} sx={{ '&.Mui-checked': { color: '#2383e2' } }} />}
                label={plat}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">All platforms have already been added.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal}>Cancel</Button>
          <Button onClick={handleAddPlatforms} disabled={selectedPlatforms.length === 0} variant="contained"
            sx={{ bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' } }}>
            Add Platforms
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Child PO Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Removal</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            Are you sure you want to remove this Purchase Order? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeletePO} color="error" variant="contained">Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterPODetails;
